import * as React from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { SQLCell } from '../code-illustrations/cells/SQLCell';
import { DataConnectionCard } from '../code-illustrations/DataConnectionCard';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 60px;
  width: 100%;
`;

const ConnectionCards = styled.div`
  position: relative;
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 280px;

  > div:not(:last-child) {
    min-height: 60px;
    width: 100%;
    position: relative;
    z-index: 3;

    &:nth-child(2) {
      position: absolute;
      top: 8px;
      left: 10px;
      right: 10px;
      opacity: 0.5;
      width: calc(100% - 20px);
      z-index: 2;
    }

    &:nth-child(3) {
      position: absolute;
      top: 0;
      left: 20px;
      right: 20px;
      opacity: 0.3;
      width: calc(100% - 40px);
      z-index: 1;
    }
  }
`;

const Highlight = styled.div<{ $isCard?: boolean }>`
  position: absolute;
  left: -15px;
  right: -15px;
  bottom: -15px;
  border-bottom: 1px solid ${({ theme }) => theme.highlightColor};
  border-left: 1px solid ${({ theme }) => theme.highlightColor};
  border-right: 1px solid ${({ theme }) => theme.highlightColor};
  height: 60px;
  background-color: ${({ theme }) => rgba(theme.highlightColor, 0.2)};

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    background: linear-gradient(
      0deg,
      ${({ theme }) => rgba(theme.colors.BLACK, 0)} 0%,
      ${({ theme }) => rgba(theme.colors.BLACK, 1)} 100%
    );
  }

  &::after {
    content: '';
    display: block;
    width: 1px;
    height: 20px;
    background: ${({ theme }) => theme.highlightColor};
    position: absolute;
    left: 50%;
    top: 100%;
  }

  ${({ $isCard }) =>
    $isCard &&
    css`
      bottom: auto;
      top: -15px;
      border-top: 1px solid ${({ theme }) => theme.highlightColor};
      border-bottom: none;

      &::before {
        background: linear-gradient(
          180deg,
          ${({ theme }) => rgba(theme.colors.BLACK, 0)} 0%,
          ${({ theme }) => rgba(theme.colors.BLACK, 1)} 100%
        );
      }

      &::after {
        bottom: 100%;
        top: auto;
      }
    `}
`;

const QueryCell = styled.div<{ $hideConnection: boolean }>`
  width: ${({ $hideConnection }) => ($hideConnection ? '100%' : '75%')};
  position: relative;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    width: 100%;
  }
`;
interface DataConnectionIllyProps {
  defaultResultType?: 'chart' | 'table';
  hideConnection?: boolean;
  hideOutputToggle?: boolean;
  name: string;
  withdbt?: boolean;
}

export const DataConnectionIlly: React.ComponentType<DataConnectionIllyProps> =
  React.memo(
    ({
      name,
      withdbt = false,
      hideConnection = false,
      hideOutputToggle = false,
      defaultResultType = 'chart'
    }) => {
      const queryCell = (
        <QueryCell $hideConnection={hideConnection}>
          {!hideConnection && <Highlight $isCard={true} />}
          <SQLCell
            defaultResultType={defaultResultType}
            hideHeader={true}
            hideOutputToggle={hideOutputToggle}
            name={name}
          />
        </QueryCell>
      );

      return (
        <Wrapper>
          {!hideConnection && (
            <ConnectionCards>
              <DataConnectionCard name={name} withdbt={withdbt} />
              <Highlight />
            </ConnectionCards>
          )}
          {queryCell}
        </Wrapper>
      );
    }
  );
